import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import ResumeSessionCard from "../components/ResumeSessionCard";
import SessionsList from "../components/SessionsList";
import SessionDetailSheet from "../components/SessionDetailSheet";
import HabitNotePrompt from "../components/HabitNotePrompt";
import TraceReports from "../components/TraceReports";
import { WaitlistBlock } from "../components/Waitlist";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { getProject, getResume, listSessions, syncProject, deleteProject } from "../lib/api";import { ExternalLink, GitBranch, RefreshCw, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fmtRelative } from "../lib/format";
import { useDemoMode } from "../lib/demo";

export default function ProjectPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isWelcome = searchParams.get("welcome") === "1";
  const demo = useDemoMode();
  const [project, setProject] = useState(null);
  const [resume, setResume] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [openSheet, setOpenSheet] = useState(false);
  const [activeSession, setActiveSession] = useState(null);

  const load = useCallback(async () => {
    const [p, r, s] = await Promise.all([
      getProject(projectId),
      getResume(projectId),
      listSessions(projectId),
    ]);
    setProject(p);
    setResume(r);
    setSessions(s);
  }, [projectId]);

  useEffect(() => {
    load().catch(() => toast.error("Failed to load project"));
  }, [load]);

  // Return toast — fired once per visit when arriving via welcome=1
  useEffect(() => {
    if (isWelcome && resume?.has_session) {
      const toastKey = `trace_welcomed_${projectId}_${resume.session?.id}`;
      if (!sessionStorage.getItem(toastKey)) {
        sessionStorage.setItem(toastKey, "1");
        toast.success("Picked up right where you left off.", { duration: 3500 });
      }
    }
  }, [isWelcome, resume?.has_session, resume?.session?.id, projectId]);

  const onSync = async () => {
    setSyncing(true);
    try {
      await syncProject(projectId);
      await load();
      const after = await listSessions(projectId);
      const found = (after || []).length;
      if (found > 0) {
        toast.success(`Session captured. We'll remember this. (${found} sessions)`);
      } else {
        toast.success("Project synced");
      }
    } catch {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const onDelete = async () => {
    try {
      await deleteProject(projectId);
      toast.success("Project removed");
      navigate("/");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!project) {
    return (
      <AppShell>
        <div className="p-12 text-[#6b7280] flex items-center gap-2">
          <Loader2 className="animate-spin" size={16} /> Loading project…
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-6 sm:py-10">
        {isWelcome && resume?.has_session && (
          <div className="mb-6 trace-fade-in" data-testid="welcome-banner">
            <span className="text-[11px] uppercase tracking-[0.18em] text-[#6b7280]">
              Welcome back
            </span>
            <h2 className="text-2xl sm:text-3xl tracking-tight text-[#111827] mt-1 leading-snug">
              You're back. <span className="gradient-text">Here's where you left off.</span>
            </h2>
          </div>
        )}

        <div className="flex items-start justify-between gap-3 mb-8 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#6b7280]">Project</div>
            <h1 className="text-2xl sm:text-3xl tracking-tight text-[#111827] mt-1.5 break-words" data-testid="project-name">
              {project.name}
            </h1>
            <div className="text-sm text-[#6b7280] flex items-center gap-x-3 gap-y-1 mt-1 flex-wrap">
              <a
                href={project.repo_url}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-xs flex items-center gap-1.5 hover:text-[#111827] transition truncate max-w-full"
                data-testid="repo-link"
              >
                <span className="truncate">{project.full_name}</span> <ExternalLink size={12} className="shrink-0" />
              </a>
              <span className="text-xs flex items-center gap-1">
                <GitBranch size={12} /> {project.default_branch}
              </span>
              {project.last_synced_at && !demo && (
                <span className="text-[11px] text-[#9ca3af]">
                  synced {fmtRelative(project.last_synced_at)}
                </span>
              )}
            </div>
          </div>
          {!demo && (
            <div className="flex items-center gap-2 shrink-0">
            <button
              data-testid="project-sync"
              onClick={onSync}
              disabled={syncing}
              className="px-3 py-2 rounded-md bg-white border border-[#e5e7eb] text-sm hover:bg-[#f7f8fa] transition disabled:opacity-60 inline-flex items-center gap-1.5"
            >
              {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              {syncing ? "Syncing" : "Sync"}
            </button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  data-testid="project-delete"
                  className="px-3 py-2 rounded-md bg-white border border-[#e5e7eb] text-sm hover:bg-[#f7f8fa] transition inline-flex items-center gap-1.5 text-[#6b7280]"
                  aria-label="Delete project"
                >
                  <Trash2 size={14} />
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Forget this project?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Trace will remove its memory of this repo (sessions, file events). Your repo on
                    GitHub is untouched.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="delete-confirm" onClick={onDelete}>
                    Forget
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
          )}
        </div>

        <ResumeSessionCard
          project={project}
          session={resume?.has_session ? resume.session : null}
          onNoteSaved={load}
          onResume={(s) => {
            if (demo) return; // stay on the hero card during recording
            setActiveSession(s);
            setOpenSheet(true);
          }}
          onViewTimeline={() => navigate(`/project/${projectId}/timeline`)}
          welcome={isWelcome}
          demo={demo}
        />

        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl tracking-tight text-[#111827]">Recent sessions</h2>
            <span className="text-xs text-[#6b7280]">{sessions.length} total</span>
          </div>
          <SessionsList
            sessions={sessions.slice(0, 8)}
            onSelect={(s) => {
              setActiveSession(s);
              setOpenSheet(true);
            }}
          />
        </div>

        <SessionDetailSheet
          projectId={projectId}
          sessionId={activeSession?.id}
          open={openSheet}
          onOpenChange={(o) => {
            setOpenSheet(o);
            // Clear welcome flag once user engages
            if (o && isWelcome) {
              const next = new URLSearchParams(searchParams);
              next.delete("welcome");
              setSearchParams(next, { replace: true });
            }
          }}
          onSaved={load}
        />

        <TraceReports projectId={projectId} projectName={project.name} />

        {!demo && <WaitlistBlock repoFullName={project.full_name} />}

        {!demo && (
          <HabitNotePrompt
            projectId={projectId}
            session={resume?.has_session ? resume.session : null}
            onSaved={load}
          />
        )}
      </div>
    </AppShell>
  );
}
